import React from 'react';
import styled from '@emotion/styled';
import { FieldPath, FieldValues, get, PathValue, useController } from 'react-hook-form';
import { DropdownProps, Select, SelectProps } from 'semantic-ui-react';
import { FormField } from '@britishcouncil/react-admin';

import { useFormContext } from './FormContext';
import { ReadonlyFormValue } from './ReadonlyFormValue';
import { RHFormSelectProps } from './RHFormSelect';

export const RHFormSelectWithAddon = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  onChange,
  readonlyMode,
  placeholder,
  renderReadonlyValue,
  addon,
  ...props
}: Omit<RHFormSelectProps<TFieldValues, TName>, 'icon'> & {
  addon: (value: PathValue<TFieldValues, TName>) => React.ReactNode;
}) => {
  const { mode } = useFormContext();
  const { field, formState } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  const handleOnChange = (_event: React.SyntheticEvent<HTMLElement>, { value }: DropdownProps) => {
    // null is returned when clearing the select and additional check is needed to not return empty string
    const isEmpty = value === '' || value === undefined;
    field.onChange(!isEmpty ? value : null);
    onChange?.((!isEmpty ? value : null) as PathValue<TFieldValues, TName>);
  };

  if (mode === 'read' || readonlyMode) {
    return (
      <ReadonlyFormValue
        label={props.label}
        value={field.value}
        renderValue={(value) => {
          const option = props.options?.find((opt) => opt.value === value);
          return renderReadonlyValue ? (
            <S.ReadonlyWrapper>
              {addon(field.value)} {renderReadonlyValue(value, option)}
            </S.ReadonlyWrapper>
          ) : (
            <S.ReadonlyWrapper>
              {addon(field.value)} {option ? option.text : (placeholder ?? '-')}
            </S.ReadonlyWrapper>
          );
        }}
      />
    );
  }

  return (
    <FormField
      {...field}
      {...props}
      addon={addon(field.value)}
      control={SelectWithIcon}
      placeholder={placeholder ?? 'None selected'}
      name={name}
      value={field.value}
      onChange={handleOnChange}
      onBlur={field.onBlur}
      touched
      error={get(formState.errors, name)?.message}
    />
  );
};

const SelectWithIcon = ({
  addon,
  ...props
}: SelectProps & {
  addon: React.ReactNode;
}) => {
  return (
    <S.Wrapper>
      <S.Addon>{addon}</S.Addon>
      <S.Select {...props} />
    </S.Wrapper>
  );
};

const S = {
  Wrapper: styled.div`
    display: flex;
  `,
  ReadonlyWrapper: styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
  `,
  Addon: styled.div`
    border: 1px solid rgba(34, 36, 38, 0.15);
    border-top-left-radius: 0.28571429rem;
    border-bottom-left-radius: 0.28571429rem;
    display: inline-block;
    padding: 0 0.5em;
    display: flex;
    align-items: center;
  `,
  Select: styled(Select)`
    &.ui.dropdown {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
  `,
};
